import type {ReactNode} from 'react'
import React, {useState, useRef, useEffect} from 'react'
import {CSSTransition} from 'react-transition-group'
import {pushToDataLayer} from '@/utilities/analytics'
import {TextButton} from '@/products/flexible-web-toolkit/components/button/text-button'
import {Button} from '@/products/flexible-web-toolkit/components/button/button'
import {Modal} from './sidebar-modal'

interface ModalCta {
	text: string
	variant: {
		choice: string | undefined
	}
}
interface SecondaryCta {
	url: string
	openLinkInNewTab: {
		boolean: boolean
	}
	variant: {
		choice: string | undefined
	}
	text: string
}
interface TextLink {
	url: string
	openInNewTab: boolean
	text: string
}
interface SidepanelProps {
	className?: string
	title?: string
	modalCta?: ModalCta | null
	secondaryCta: SecondaryCta | null | undefined
	textLink: TextLink | null
	summaryText?: string
	reportedCases?: string | number
	reportedTextAbove?: string
	reportedTextBelow?: string
	modalTitle?: string
	modalSubtitle?: string
	theme?: string
	children?: ReactNode | null
}

export const Sidepanel = ({
	className = '',
	title = '',
	modalCta,
	secondaryCta,
	textLink,
	summaryText = '',
	reportedCases,
	reportedTextAbove = '',
	reportedTextBelow = '',
	modalTitle = '',
	modalSubtitle = '',
	theme = '#2772ce',
	children = null,
}: SidepanelProps): React.JSX.Element => {
	const [showModal, setShowModal] = useState(false)
	const nodeRef = useRef(null)

	const previousReportedRef = useRef<{
		cases?: string | number
		country?: string
	}>({})

	const debounceTimer = useRef<NodeJS.Timeout | null>(null)

	useEffect(() => {
		if (!reportedCases || !summaryText || !reportedTextAbove) return

		const reportedCasesNow = reportedCases
		const reportedTextAboveNow = reportedTextAbove

		if (
			previousReportedRef.current.cases === reportedCasesNow &&
			previousReportedRef.current.country === reportedTextAboveNow
		) {
			return
		}

		previousReportedRef.current = {
			cases: reportedCasesNow,
			country: reportedTextAboveNow,
		}

		if (debounceTimer.current) {
			clearTimeout(debounceTimer.current)
		}

		debounceTimer.current = setTimeout(() => {
			pushToDataLayer({
				event: 'reportedCases',
				reportedCases: reportedCasesNow,
				country: reportedTextAboveNow,
			})
		}, 300)
	}, [reportedCases, summaryText, reportedTextAbove])

	const toggleModal = (): void => {
		if (!showModal) {
			pushToDataLayer({
				event: 'cta_click',
				cta_name: modalCta?.text,
				cta_category: 'CTA_Button',
			})
		}
		setShowModal(!showModal)
	}
	return (
		<>
			<aside className={className}>
				<div className="flex flex-col">
					<div
						className="flex items-center justify-start space-x-4 p-6"
						style={{backgroundColor: theme}}
					>
						<svg
							data-name="Group 16853"
							height="60"
							id="Group_16853"
							viewBox="0 0 40 60"
							width="40"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M20,0A20.082,20.082,0,0,1,40,20.164C40,31.3,20,60,20,60S0,31.3,0,20.164A20.082,20.082,0,0,1,20,0Z"
								data-name="Path 11480"
								fill="#fff"
								id="Path_11480"
							/>
							<circle
								cx="16"
								cy="16"
								data-name="Ellipse 91498"
								fill={theme}
								id="Ellipse_91498"
								r="16"
								transform="translate(4 4)"
							/>
						</svg>
						<h3 className="text-xl text-white md:text-2xl">
							{title}
						</h3>
					</div>
					<div className="flex flex-wrap items-center justify-start p-6 pb-0 md:space-x-4">
						{modalCta ? (
							<Button
								as="button"
								className="mr-4"
								onClick={toggleModal}
								variant={modalCta.variant.choice}
							>
								{modalCta.text}
							</Button>
						) : null}
						{secondaryCta ? (
							<Button
								as="a"
								href={secondaryCta.url}
								target={
									secondaryCta.openLinkInNewTab.boolean
										? '_blank'
										: '_self'
								}
								variant={secondaryCta.variant.choice}
							>
								{secondaryCta.text}
							</Button>
						) : null}
						{textLink ? (
							<TextButton
								as="a"
								className="ml-0 px-3 py-5 not-italic"
								href={textLink.url}
								target={
									textLink.openInNewTab ? '_blank' : '_self'
								}
								variant="secondary"
							>
								{textLink.text}
							</TextButton>
						) : null}
					</div>
					<div className="flex w-full flex-col justify-center space-y-4 p-6 md:p-6">
						{reportedCases ? (
							<div className="flex flex-col space-y-2">
								{reportedTextAbove ? (
									<p className="text-center font-semibold">
										{reportedTextAbove}
									</p>
								) : null}
								<span className="text-center text-7xl">
									<strong>{reportedCases}</strong>
								</span>
								{reportedTextBelow ? (
									<p className="text-center font-semibold">
										{reportedTextBelow}
									</p>
								) : null}
							</div>
						) : null}
						<div
							className="content w-full"
							dangerouslySetInnerHTML={{__html: summaryText}}
						/>
					</div>
				</div>
			</aside>
			<CSSTransition
				classNames="popup"
				in={showModal}
				nodeRef={nodeRef}
				timeout={500}
				unmountOnExit
			>
				<Modal
					subtitle={modalSubtitle}
					title={modalTitle}
					togglePopup={toggleModal}
				>
					{children}
				</Modal>
			</CSSTransition>
		</>
	)
}
